import { TeamInLeague, Fixture, Player, Result } from '../domain/entities';
import { FlavorTextService } from './FlavorTextService';

const getTeamAvgSkill = (team: TeamInLeague, lineup: number[]): number => {
    if (!team.squad || lineup.length === 0) return team.baseSkill;
    const lineupPlayers = team.squad.filter(p => lineup.includes(p.id));
    const totalSkill = lineupPlayers.reduce((sum, player) => sum + player.skill + player.form, 0);
    return totalSkill / lineupPlayers.length;
};

const pickRandom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

const generateOfflineHeadline = (result: Result, homeTeamName: string, awayTeamName: string): string => {
    const { homeScore, awayScore, isSignificant } = result;
    const homeTeamShort = homeTeamName.split(' ').pop();
    const awayTeamShort = awayTeamName.split(' ').pop();
    
    if (isSignificant && homeScore + awayScore >= 6) {
        return "THRILLER!";
    }
    if (isSignificant) {
        return "STUNNING UPSET!";
    }
    if (homeScore > awayScore) {
        if (homeScore - awayScore >= 3) return `${homeTeamShort} cruise to victory`.toUpperCase();
        return `${homeTeamShort} claim the win`.toUpperCase();
    }
    if (awayScore > homeScore) {
        if (awayScore - homeScore >= 3) return `${awayTeamShort} dominate away`.toUpperCase();
        return `${awayTeamShort} win on the road`.toUpperCase();
    }
    return "Honours Even".toUpperCase();
};


const generateMatchReport = (homeTeam: TeamInLeague, awayTeam: TeamInLeague, fixture: Fixture, getPlayerById: (id: number) => Player | undefined): string => {
    if (!fixture.result) {
        return "Match has not been played yet.";
    }

    const {
        homeScore, awayScore, homeScorers, awayScorers, homeStartingXI, awayStartingXI,
        homePossession, homeShots,
        homePlayerRatings, awayPlayerRatings, events
    } = fixture.result;

    const homeAvgSkill = getTeamAvgSkill(homeTeam, homeStartingXI);
    const awayAvgSkill = getTeamAvgSkill(awayTeam, awayStartingXI);
    const skillDiff = homeAvgSkill - awayAvgSkill;
    const report: string[] = [];

    const allRatings = [
        ...Object.entries(homePlayerRatings).map(([id, rating]) => ({ id: Number(id), rating, team: homeTeam })),
        ...Object.entries(awayPlayerRatings).map(([id, rating]) => ({ id: Number(id), rating, team: awayTeam }))
    ];
    allRatings.sort((a, b) => b.rating - a.rating);
    const motmData = allRatings.length > 0 ? allRatings[0] : null;
    const motm = motmData ? getPlayerById(motmData.id) : undefined;
    
    const countGoals = (scorerIds: number[]) => {
        const goalCounts: { [id: number]: number } = {};
        scorerIds.forEach(id => { goalCounts[id] = (goalCounts[id] || 0) + 1; });
        return goalCounts;
    };
    const homeGoalCounts = countGoals(homeScorers);
    const awayGoalCounts = countGoals(awayScorers);

    const goalDifference = Math.abs(homeScore - awayScore);

    if (homeScore > awayScore) { // Home Win
        if (goalDifference >= 3) {
            report.push(pickRandom([
                `${homeTeam.name} produced a scintillating display to thrash ${awayTeam.name} ${homeScore}-${awayScore} at home.`,
                `A dominant ${homeTeam.name} side proved too strong for the visitors, cruising to a comfortable ${homeScore}-${awayScore} victory.`
            ]));
        } else if (skillDiff < -5) { // Upset
            report.push(pickRandom([
                `${homeTeam.name} pulled off a stunning upset, defeating a much-fancied ${awayTeam.name} side ${homeScore}-${awayScore}.`,
                `Against all odds, ${homeTeam.name} secured a famous ${homeScore}-${awayScore} victory over ${awayTeam.name}.`
            ]));
        } else {
            report.push(pickRandom([
                `In a hard-fought contest, ${homeTeam.name} edged out ${awayTeam.name} with a narrow ${homeScore}-${awayScore} win.`,
                `${homeTeam.name} held their nerve to secure a vital ${homeScore}-${awayScore} victory in front of their home fans.`
            ]));
        }
    } else if (awayScore > homeScore) { // Away Win
        if (goalDifference >= 3) {
            report.push(pickRandom([
                `${awayTeam.name} put on a clinical away performance to dismantle ${homeTeam.name} ${awayScore}-${homeScore}.`,
                `The travelling fans were in dreamland as ${awayTeam.name} romped to a ${awayScore}-${homeScore} win.`
            ]));
        } else if (skillDiff > 5) { // Upset
            report.push(pickRandom([
                `${awayTeam.name} silenced the home crowd with a shock ${awayScore}-${homeScore} victory over ${homeTeam.name}.`,
                `A classic smash-and-grab performance saw ${awayTeam.name} steal the points with a ${awayScore}-${homeScore} win.`
            ]));
        } else {
            report.push(pickRandom([
                `${awayTeam.name} will be delighted with their work on the road, grinding out a ${awayScore}-${homeScore} win.`,
                `A determined ${awayTeam.name} side left with all three points after a tight ${awayScore}-${homeScore} victory.`
            ]));
        }
    } else { // Draw
        if (homeScore === 0) {
            report.push(pickRandom([
                `A tense affair ended in a goalless stalemate between ${homeTeam.name} and ${awayTeam.name}, with defences on top.`,
                `Neither side could find the breakthrough in a cagey 0-0 draw.`
            ]));
        } else {
            report.push(pickRandom([
                `It was an entertaining ${homeScore}-${homeScore} draw, with both teams having to settle for a share of the spoils.`,
                `Honours were even in a thrilling ${homeScore}-${homeScore} encounter that saw the points shared.`
            ]));
        }
    }

    const managerEvent = events.find(e => e.type === 'positive' || e.type === 'negative');
    if (managerEvent) {
        report.push(managerEvent.message);
    } else if (homePossession > 65) {
        report.push(pickRandom([
            `${homeTeam.name} dominated the ball from start to finish, patiently probing for openings.`,
            `The hosts controlled the tempo of the game, enjoying the lion's share of possession.`
        ]));
    } else if (homePossession < 35) {
         report.push(pickRandom([
            `${awayTeam.name} were comfortable in possession, dictating play for large periods of the match.`,
            `The visitors saw most of the ball, forcing ${homeTeam.name} to chase shadows.`
        ]));
    } else {
         report.push(pickRandom([
            `It was an even contest in the middle of the park, with neither side able to establish true dominance.`,
            `The midfield battle was fiercely contested, resulting in a balanced share of possession.`
        ]));
    }
    
    if (motm && motmData) {
        let motmVerb = 'a stellar';
        if (motmData.rating >= 9.0) motmVerb = 'a magnificent, commanding';
        else if (motmData.rating < 7.0) motmVerb = 'a solid';
        
        const motmGoalCount = homeGoalCounts[motm.id] || awayGoalCounts[motm.id] || 0;
        let goalContribution = '';
        if (motmGoalCount > 0) {
            goalContribution = `chipping in with ${motmGoalCount} goal${motmGoalCount > 1 ? 's' : ''}`;
        } else if (motm.position === 'GK' && (motmData.team.name === homeTeam.name ? awayScore === 0 : homeScore === 0)) {
            goalContribution = 'and earning a crucial clean sheet';
        }

        report.push(pickRandom([
            `The standout performer was undoubtedly ${motm.name}, who put in ${motmVerb} performance${goalContribution ? `, ${goalContribution}` : ''}. His rating of ${motmData.rating} reflects his influence on the game.`,
            `Man of the Match honours went to ${motm.name} of ${motmData.team.name}, who was a cut above the rest with a ${motmData.rating} rating.`
        ]));
    }

    if (homeScore > awayScore) {
        report.push(pickRandom([
            `The ${homeTeam.name} manager will be thrilled with both the result and the performance.`,
            `A deserved victory for the home side, who will look to build on this momentum.`
        ]));
    } else if (awayScore > homeScore) {
        report.push(pickRandom([
            `${awayTeam.name} return home with a massive three points.`,
            `A tough result for ${homeTeam.name} to swallow, who were second-best on the day.`
        ]));
    } else {
        report.push(pickRandom([
            `Ultimately, a draw was probably a fair result on the balance of play.`,
            `Both managers may feel they could have won it, but will have to be content with a point.`
        ]));
    }

    return report.join(' ');
};

export const MatchReportService = {
    generateMatchReport,
    generateOfflineHeadline,
};
